import React, { useState, useEffect, useMemo } from "react";
import api, { HR_BASE_URL } from "../api";
import styled from "styled-components";
import { CheckSquare, Check, X, Search, Info } from "lucide-react";

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 40px);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  background: var(--glass);
  padding: 20px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  flex-wrap: wrap;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0 12px;
  flex: 1;
  min-width: 250px;
  transition: all 0.2s;
  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const StyledInput = styled.input`
  border: none;
  background: transparent;
  padding: 12px;
  color: var(--text);
  width: 100%;
  font-size: 14px;
  &:focus { outline: none; }
  &::placeholder { color: var(--muted); }
`;

const StyledSelect = styled.select`
  background: var(--bg);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  min-width: 150px;
  &:focus { outline: none; border-color: var(--primary); }
`;

const TableContainer = styled.div`
  background: var(--glass);
  backdrop-filter: blur(12px);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  background: rgba(0,0,0,0.2);
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  color: var(--text);
  font-size: 14px;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${(props) => {
    switch(props.status) {
      case "Approved": return "rgba(16, 185, 129, 0.15)";
      case "Rejected": return "rgba(239, 68, 68, 0.15)";
      default: return "rgba(245, 158, 11, 0.15)";
    }
  }};
  color: ${(props) => {
    switch(props.status) {
      case "Approved": return "#10b981";
      case "Rejected": return "#ef4444";
      default: return "#f59e0b";
    }
  }};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  
  ${(props) => props.primary ? `
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
    &:hover:not(:disabled) {
      background: rgba(16, 185, 129, 0.3);
    }
  ` : `
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.3);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function LeaveApprovals() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");

  const fetchLeaves = async () => {
    try {
      const role = localStorage.getItem("role");
      const deptId = localStorage.getItem("department_id");
      const deptName = localStorage.getItem("department_name") || localStorage.getItem("department");
      
      let url = `${HR_BASE_URL}leaves/pending/`;
      if (role !== "Admin") {
        const params = new URLSearchParams();
        if (deptName) params.append("department", deptName);
        if (deptId) params.append("department_id", deptId);
        
        const qs = params.toString();
        if (qs) url += `?${qs}`;
      }
      
      const response = await api.get(url);
      setLeaves(response.data);
    } catch (err) {
      console.error("Error fetching leaves", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleUpdateStatus = async (leaveId, status) => {
    try {
      const reviewerName = localStorage.getItem("name") || "Admin";
      await api.put(`${HR_BASE_URL}leaves/${leaveId}/status/`, { 
        status, 
        reviewer_name: reviewerName 
      });
      // Update local state to reflect change instantly
      setLeaves(leaves.map(l => l.id === leaveId ? { ...l, status, reviewed_by_name: reviewerName } : l));
    } catch (err) {
      console.error("Error updating leave status", err);
      alert("Failed to update status. Please try again.");
    }
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter(leave => {
      // Search term filter
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        (leave.employee_name && leave.employee_name.toLowerCase().includes(term)) ||
        (leave.employee_id && leave.employee_id.toLowerCase().includes(term)) ||
        (leave.department && leave.department.toLowerCase().includes(term));
        
      // Status filter
      const matchesStatus = statusFilter ? leave.status === statusFilter : true;
      
      return matchesSearch && matchesStatus;
    });
  }, [leaves, searchTerm, statusFilter]);

  return (
    <Page>
      <Container>
        <Header>
          <Title>
            <CheckSquare size={32} color="var(--primary)" />
            Leave Approvals
          </Title>
          <Subtitle>Review and manage employee leave requests</Subtitle>
        </Header>

        <FiltersContainer>
          <InputGroup style={{ flex: '1 1 250px' }}>
            <Search size={18} color="var(--muted)" />
            <StyledInput 
              type="text" 
              placeholder="Search by Employee, ID, or Department..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <StyledSelect 
            style={{ flex: '1 1 150px' }} 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </StyledSelect>
        </FiltersContainer>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Employee</Th>
                <Th>Department</Th>
                <Th>Leave Type</Th>
                <Th>Dates</Th>
                <Th>Reason</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan="7" style={{textAlign: 'center', padding: '40px'}}>Loading requests...</Td></tr>
              ) : filteredLeaves.length > 0 ? (
                filteredLeaves.map((leave) => (
                  <tr key={leave.id}>
                    <Td>
                      <strong>{leave.employee_name || 'N/A'}</strong>
                      <div style={{fontSize: '12px', color: 'var(--muted)'}}>ID: {leave.employee_id}</div>
                    </Td>
                    <Td>{leave.department || 'N/A'}</Td>
                    <Td>{leave.leave_type}</Td>
                    <Td>
                      {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                    </Td>
                    <Td style={{ maxWidth: '200px' }}>
                      <div style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }} title={leave.reason}>
                        <Info size={14} color="var(--muted)" style={{ flexShrink: 0 }}/>
                        {leave.reason}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge status={leave.status}>{leave.status}</StatusBadge>
                    </Td>
                    <Td>
                      {leave.status === "Pending" ? (
                        <Actions>
                          <ActionButton 
                            primary 
                            onClick={() => handleUpdateStatus(leave.id, "Approved")}
                          >
                            <Check size={14} /> Approve
                          </ActionButton>
                          <ActionButton 
                            onClick={() => handleUpdateStatus(leave.id, "Rejected")}
                          >
                            <X size={14} /> Reject
                          </ActionButton>
                        </Actions>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          Reviewed by {leave.reviewed_by_name || 'System'}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))
              ) : (
                <tr><Td colSpan="7" style={{textAlign: 'center', padding: '40px'}}>No leave requests found.</Td></tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Container>
    </Page>
  );
}

export default LeaveApprovals;
